import { Injectable } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject } from 'rxjs';
import { Constant } from '../common/constant';

/**
 * Thin wrapper around @stomp/stompjs, authenticated with the same JWT every other
 * HTTP call already uses (see ApiInterceptor) - passed as a "token" query parameter on the
 * WebSocket handshake URL, not a STOMP header. Browsers can't set custom headers on a native
 * WebSocket handshake, so the backend's JwtHandshakeInterceptor reads it from the URL instead;
 * that also binds the identity to the session at the handshake itself (Spring's reliable,
 * documented mechanism), rather than a STOMP CONNECT frame header, which does not.
 *
 * `onConnected` fires on the first connect AND on every automatic reconnect.
 * Subscriptions do not survive a reconnect, so the caller re-subscribes there.
 */
@Injectable({ providedIn: 'root' })
export class ChatStompService {
  private client: Client | null = null;

  readonly connected$ = new BehaviorSubject<boolean>(false);

  connect(onConnected: () => void): void {
    this.disconnect();
    const token = sessionStorage.getItem('token') || '';

    this.client = new Client({
      brokerURL: Constant.WS_URL + '?token=' + encodeURIComponent(token),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        this.connected$.next(true);
        onConnected();
      },
      onStompError: (frame) => console.error('[ChatStompService] STOMP error:', frame.headers['message']),
      onWebSocketError: (evt) => console.error('[ChatStompService] WebSocket error', evt),
      onWebSocketClose: () => this.connected$.next(false)
    });
    this.client.activate();
  }

  /**
   * Makes sure the session is open without tearing down one that already is. "Online" for other users
   * means "has an open session", so the layout opens it for the whole login (not only while the Chat
   * page is showing) and the Chat page just listens on it. Safe to call repeatedly.
   */
  ensureConnected(): void {
    if (!this.client) {
      this.connect(() => { });
    }
  }

  get isConnected(): boolean {
    return !!this.client && this.client.connected;
  }

  subscribe<T>(destination: string, callback: (body: T) => void): StompSubscription | null {
    if (!this.client || !this.client.connected) {
      console.warn('[ChatStompService] Cannot subscribe - not connected:', destination);
      return null;
    }
    return this.client.subscribe(destination, (message: IMessage) => {
      try {
        callback(JSON.parse(message.body) as T);
      } catch (err) {
        console.error('[ChatStompService] Bad message body', err);
      }
    });
  }

  send(destination: string, payload: unknown): boolean {
    if (!this.client || !this.client.connected) {
      console.warn('[ChatStompService] Cannot send - not connected:', destination);
      return false;
    }
    this.client.publish({ destination, body: JSON.stringify(payload) });
    return true;
  }

  disconnect(): void {
    if (this.client) {
      void this.client.deactivate();
      this.client = null;
    }
    this.connected$.next(false);
  }
}
